import { useCallback } from 'react';
import { Stack } from '@mantine/core';
import InboxFilterBar from '@components/InboxReviews/InboxFilterBar';
import InboxSkeletonLoader from '@components/InboxReviews/InboxSkeletonLoader';
import DirectMessagesTable from '@components/InboxReviews/Tables/DirectMessagesTable';
import ThPagination from '@components/ThPagination/ThPagination';
import {
  useWorkerDirectMessages,
  useMarkDirectChatAsRead,
  useMarkDirectChatAsUnread,
} from '@worker/hooks/useWorkerInbox';
import { useWorkerInboxQueryState } from '@worker/hooks/useWorkerInboxQueryState';
import { useChatStore } from '@stores/chatStore';
import { useUserContext } from '@state/userContext';
import type { DirectChat } from '@th-types/inbox-reviews.type';
import { InboxChatModeEnum } from '@th-types/inbox-reviews.type';
import { ChatType, ChatEntityType } from '@th-types/chat.type';
import { isAnnouncementChat } from '@constants/directChat';
import { useTableSelection } from '@hooks/useTableSelection';
import { getDirectChatId } from '@components/InboxReviews/inboxSelectionHelpers';
import {
  InboxSearchParams,
  handleInboxClear,
  handleInboxPageChange,
  handleInboxSearch,
  handleInboxToggleUnread,
  handleInboxChatModeChange,
} from '@utils/InboxHelper';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';

export default function DirectMessagesTab() {
  const { searchParams, setSearchParams, urlParams } =
    useWorkerInboxQueryState();

  const currentOffset = urlParams.offset ?? 0;
  const showOnlyUnread = urlParams.showOnlyNew ?? false;
  const chatMode = (urlParams.mode as InboxChatModeEnum) ?? null;

  const openChat = useChatStore((state) => state.openChat);
  const { id: currentUserId } = useUserContext();
  const { mutate: markAsRead } = useMarkDirectChatAsRead();
  const { mutate: markAsUnread } = useMarkDirectChatAsUnread();
  const selection = useTableSelection<DirectChat>(getDirectChatId);

  const handleToggleRead = useCallback(
    (chat: DirectChat) => {
      if (chat.unreadMessages > 0) {
        markAsRead(chat.chatId);
      } else {
        markAsUnread(chat.chatId);
      }
    },
    [markAsRead, markAsUnread]
  );

  const handleOpenChat = useCallback(
    (chat: DirectChat) => {
      if (chat.unreadMessages > 0) {
        markAsRead(chat.chatId);
      }
      openChat(
        {
          chatType: ChatType.DIRECT,
          jobAssignmentId: chat.chatId,
          unreadMessages: chat.unreadMessages,
          firstUnreadMessageId: chat.firstUnreadMessageId ?? null,
          isAnnouncement: isAnnouncementChat(chat),
          entity: {
            id: chat.companyId ?? 0,
            type: ChatEntityType.COMPANY,
            name: chat.companyName,
            profileImageUrl: chat.companyLogoUrl,
          },
        },
        currentUserId
      );
    },
    [openChat, currentUserId, markAsRead]
  );

  const { data, isLoading } = useWorkerDirectMessages(
    currentOffset,
    DEFAULT_ITEMS_PER_PAGE,
    urlParams.searchState?.searchText,
    urlParams.searchState?.dateRange,
    showOnlyUnread || undefined,
    chatMode ?? undefined
  );

  const handleSearch = useCallback(
    (search: InboxSearchParams) => {
      handleInboxSearch(search, searchParams, setSearchParams, {
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      });
    },
    [searchParams, setSearchParams]
  );

  const handleClear = useCallback(() => {
    handleInboxClear(setSearchParams, {
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    });
  }, [setSearchParams]);

  /**
   * Bulk mark handlers fire one mutation per chat.
   * Replace with a single bulk endpoint call when BE supports it.
   */
  const handleMarkAllAsRead = useCallback(() => {
    const unreadChats = (data?.directChats ?? []).filter(
      (chat) => chat.unreadMessages > 0
    );
    unreadChats.forEach((chat) => markAsRead(chat.chatId));
  }, [data, markAsRead]);

  const handleMarkAllAsUnread = useCallback(() => {
    const readChats = (data?.directChats ?? []).filter(
      (chat) => chat.unreadMessages === 0
    );
    readChats.forEach((chat) => markAsUnread(chat.chatId));
  }, [data, markAsUnread]);

  const handleMarkSelectionAsRead = useCallback(() => {
    const selected = (data?.directChats ?? []).filter((chat) =>
      selection.selectedIds.has(getDirectChatId(chat))
    );
    selected.forEach((chat) => markAsRead(chat.chatId));
  }, [data, selection, markAsRead]);

  const handleMarkSelectionAsUnread = useCallback(() => {
    const selected = (data?.directChats ?? []).filter((chat) =>
      selection.selectedIds.has(getDirectChatId(chat))
    );
    selected.forEach((chat) => markAsUnread(chat.chatId));
  }, [data, selection, markAsUnread]);

  const directMessages = data?.directChats ?? [];

  return (
    <Stack gap="md" w="100%" p={{ base: 'xs', sm: 'md' }}>
      <InboxFilterBar
        onSearch={handleSearch}
        onClear={handleClear}
        onToggleUnread={() =>
          handleInboxToggleUnread(showOnlyUnread, searchParams, setSearchParams)
        }
        showOnlyUnread={showOnlyUnread}
        chatMode={chatMode}
        onChatModeChange={(mode) =>
          handleInboxChatModeChange(mode, searchParams, setSearchParams)
        }
        onMarkAllAsRead={handleMarkAllAsRead}
        onMarkAllAsUnread={handleMarkAllAsUnread}
        selectedCount={selection.selectedCount}
        onMarkSelectionAsRead={handleMarkSelectionAsRead}
        onMarkSelectionAsUnread={handleMarkSelectionAsUnread}
      />

      {isLoading ? (
        <InboxSkeletonLoader />
      ) : (
        <>
          <DirectMessagesTable
            directMessages={directMessages}
            onOpenChat={handleOpenChat}
            onToggleRead={handleToggleRead}
            selectionProps={selection.getSelectionProps(directMessages)}
            getCompanyHref={(chat) =>
              !isAnnouncementChat(chat) && chat.profileURLName
                ? `${import.meta.env.VITE_SERVER_BASE_URL}/companies/${chat.profileURLName}`
                : undefined
            }
          />
          {data?.pagination &&
            data.pagination.count > DEFAULT_ITEMS_PER_PAGE && (
              <ThPagination
                currentOffset={currentOffset}
                itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
                totalItems={data.pagination.count}
                onPageChange={(offset) =>
                  handleInboxPageChange(offset, searchParams, setSearchParams)
                }
              />
            )}
        </>
      )}
    </Stack>
  );
}
